import {useContext, useEffect, useMemo} from 'react';

import type {ServiceIdentifier} from 'inversify';

import type {Destructible} from '../../utils/store';

import {InjectionContext} from '../components/InjectionContext';
import {ROOT_CONTAINER_KEY} from '../utils';

export const useInjection = <
  T extends Record<string, any> & Partial<Destructible>,
>(
  identifier: ServiceIdentifier<T>,
  containerKey: string = ROOT_CONTAINER_KEY,
) => {
  const context = useContext(InjectionContext);
  const container = context?.get(containerKey);

  if (!container) {
    throw new Error(
      `${containerKey} container not found in InjectionContext. Looks like you forgot to add it to the context.`,
    );
  }

  const instance = useMemo(
    () => container.get<T>(identifier),
    [container, identifier],
  );

  useEffect(() => {
    return () => {
      instance?.destruct?.();
    };
  }, [instance]);

  return instance;
};
